import React, { forwardRef } from "react";
import { bool, oneOf, string } from "prop-types";

import componentSizes from "../constants/componentSizes";
import useImageErrorFallback from "./hooks/useImageErrorFallback";
import { defaultConsumerImageSrc, defaultPartnerImageSrc } from "./assets";
import * as S from "./styled";
import userTypes from "./userTypes";

const defaultImageSrc = userType =>
  userType === userTypes.CONSUMER
    ? defaultConsumerImageSrc
    : defaultPartnerImageSrc;

/**
 * An avatar acts as a proxy for an agent, customer, building, or other image
 * in our product.
 * */
const Avatar = forwardRef(
  (
    { alt, className, isPro, size, src, userType, ...passThroughProps },
    ref
  ) => {
    const showProLabel = isPro && !(size === componentSizes.XS);
    const ImageComponent = src ? S.AvatarImg : S.DefaultImg;
    const defaultImage = defaultImageSrc(userType);
    const imageSrc = src || defaultImage;
    const [setImageEl] = useImageErrorFallback(defaultImage);

    return (
      <S.Container ref={ref} className={className} {...passThroughProps}>
        <ImageComponent ref={setImageEl} alt={alt} size={size} src={imageSrc} />
        {showProLabel && <S.ProLabel size={size}>Pro</S.ProLabel>}
      </S.Container>
    );
  }
);

Avatar.displayName = "Avatar";

Avatar.propTypes = {
  /**
   * The alt attribute for the Avatar image. Avatars are often presentational,
   * so consider leaving this string empty if information about this component's
   * meaning is provided by adjacent text.
   * */
  alt: string,
  className: string,
  /**
   * Determines if the PRO badge should be displayed. Note: the PRO badge
   * will not be displayed on Avatars with the size XS, even if this prop is
   * set to true.
   * */
  isPro: bool,
  /**
   * Determines the size of the Avatar and PRO badge when present.
   *
   * Values for the size prop are imported from the componentSizes module.
   *
   * ```
   * import Avatar from "./Avatar";
   * ```
   * ```
   * import { LG } from "./path/to/componentSizes";
   *```
   *```
   * export const MyAvatar = () => <Avatar size={LG} />;
   * ```
   * */
  size: oneOf([
    componentSizes.XS,
    componentSizes.SM,
    componentSizes.MD,
    componentSizes.LG
  ]),
  /**
   * The src attribute for the Avatar image. If a value is not provided for this
   * prop, a default image will be used instead. Which default image is used
   * depends on the value of the userType prop.
   * */
  src: string,
  /**
   * Determines the default image to be used when a value is not passed to the
   * src prop.  Values for userType are imported from the userTypes module.
   *
   * ```
   * import Avatar from "./Avatar";
   * ```
   * ```
   * import { PARTNER } from "./userTypes";
   *```
   *```
   * export const MyPartnerAvatar = () => <Avatar userType={PARTNER} />;
   * ```
   * */
  userType: oneOf(Object.values(userTypes))
};

Avatar.defaultProps = {
  alt: "",
  className: "",
  isPro: false,
  size: componentSizes.SM,
  src: null,
  userType: userTypes.CONSUMER
};
export default Avatar;
